import React from 'react';
import { connect } from 'react-redux';

import first from 'lodash/first';

import BreadcrumbNavigation from './breadcrumb_navigation.jsx';

import {
  getIsHeaderEditorOpen,
  getIsOpenPageTitleEditor,
  getCurrentlyEditingSection,
  getCurrentlyEditingContentItems,
  getTemporaryMenuData,
  getTemporaryFroalaLinkData,
  getIsThemeSelectorOpen,
  getIsStyleSelectorOpen,
  getIsPageScopedCssEditorOpen,
  getIsTranslationsEditorOpen
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  getCurrentSubpage,
  getSubpageDisplayName 
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  setEditingContent,
  closeAllEditingFeatures 
} from 'zoomforth_codesbane_editor/action_creators/misc.js';

import {
  closeThemeSelector,
  closePageScopedCssEditor
} from 'zoomforth_codesbane_editor/action_creators/theme.js';

import {
  closeMenuItemEditor
} from 'zoomforth_codesbane_editor/action_creators/navigation.js';

import {
  closePageTitleEditor
} from 'zoomforth_codesbane_editor/action_creators/header.js';

import {
  getLongDisplayName
} from 'zoomforth_codesbane_viewer/lib/sections.js';

import {
  isExternalLink
} from 'zoomforth_codesbane_viewer/lib/app_actions.js';

import {
  getDisplayableTileType
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  SECTION_TYPE_ACCORDION
} from 'zoomforth_codesbane_viewer/const/sections.js';

const mapStateToProps = (state) => {
  const currentSubpage = getCurrentSubpage(state);
  const currentSection = getCurrentlyEditingSection(state);

  // This is a precaution if we somewhere forget to remove currentContentItems from redux state
  const currentContentItems = currentSection?.type === 'tabs' ? [] : getCurrentlyEditingContentItems(state);
  const currentMenuData = getTemporaryMenuData(state);
  const currentMenuItem = currentMenuData && currentMenuData.menuItem;
  const currentFroalaLinkData = getTemporaryFroalaLinkData(state);

  const subpageDisplayName = getSubpageDisplayName(state, currentSubpage);

  const isEditingPageTitle = getIsOpenPageTitleEditor(state);
  const isEditingHeader = !!getIsHeaderEditorOpen(state);
  const isEditingSection = !!currentSection;
  const isEditingContent = currentContentItems.length > 0;
  const isEditingMultipleContentItems = currentContentItems.length > 1;
  const isEditingMenuItem = !!currentMenuData;
  const isEditingFroalaLinkData = !!currentFroalaLinkData;
  const isSelectingTheme = !!getIsThemeSelectorOpen(state);
  const isManagingStyle = !!getIsStyleSelectorOpen(state);
  const isEditingPageScopedCss = !!getIsPageScopedCssEditorOpen(state);
  const isEditingTranslations = !!getIsTranslationsEditorOpen(state);

  var contentType, sectionType, menuItemType;

  if (isEditingSection) {
    sectionType = getLongDisplayName(currentSection);
  }

  if (isEditingContent && 
      // HACK: we test for `currentSection` because,
      // there is sometimes a moment when we appear to be editing 
      // content, but are not editing a section. It occurred when closing 
      // the internal target chooser for a *menu item*. Yeah, I'm confused to.
      // 
      currentSection 
    ) {
    const contentItem = first(currentContentItems);

    if (currentSection.type === 'slideshow') {
      contentType = 'Slide';

    } else if (currentSection.type === SECTION_TYPE_ACCORDION) {
      contentType = 'Accordion Item';

    } else if (currentSection.type === 'videoplaylist') {
      contentType = 'Video';

    } else if (currentSection.type === 'tiles') {
      contentType = `${getDisplayableTileType(contentItem)} Tile`;

    }
  }

  if (isEditingFroalaLinkData && contentType) {
    contentType = `${contentType} ${currentFroalaLinkData.isButton ? 'Button' : 'Link'}`;
  }

  if (isEditingMenuItem) {
    if (currentMenuItem.isPrimaryForSubpage) {
      menuItemType = 'Subpage';

    } else if (currentMenuItem.isSubmenuItem) {
      menuItemType = 'Anchor Link';

    } else if (isExternalLink(currentMenuItem.appAction)) {
      menuItemType = 'External Link';

    } else {
      menuItemType = 'Internal Link';

    }
  }

  return {
    // actual objects
    currentSubpage,
    currentMenuItem,
    currentSection,
    currentContentItems,

    // boolean switches
    isEditingPageTitle,
    isEditingHeader,
    isEditingSection,
    isEditingContent,
    isEditingMultipleContentItems,
    isEditingMenuItem,
    isSelectingTheme,
    isManagingStyle,
    isEditingPageScopedCss,
    isEditingTranslations,

    // displayable content types
    subpageDisplayName,
    contentType,
    sectionType,
    menuItemType
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onHomeCrumbClick: () => {
      dispatch(closeAllEditingFeatures());
    },
    onNavigationCrumbClick: () => {
      dispatch(closeMenuItemEditor());
    },
    onStyleCrumbClick: () => {
      dispatch(closeThemeSelector());
      dispatch(closePageScopedCssEditor());
    },
    onSectionCrumbClick: () => {
      dispatch(setEditingContent([]));
    },
    onHeaderCrumbClick: () => {
      dispatch(closePageTitleEditor());
    }
  };
};

function generateBreadcrumbs(props) {
  const {
    isEditingPageTitle,
    isEditingHeader,
    isEditingSection,
    isEditingContent,
    isEditingMultipleContentItems,
    isEditingMenuItem,
    isSelectingTheme,
    isManagingStyle,
    isEditingPageScopedCss,
    isEditingTranslations,
    contentType,
    sectionType,
    menuItemType,
    subpageDisplayName,
    onHomeCrumbClick,
    onNavigationCrumbClick,
    onSectionCrumbClick,
    onHeaderCrumbClick,
    onStyleCrumbClick
  } = props;

  const pageEditorCrumb = {
    content: 'Site Editor',
    onClick: onHomeCrumbClick
  };

  const subpageCrumb = {
    content: subpageDisplayName,
    onClick: onHomeCrumbClick
  };

  const sectionCrumb = {
    content: sectionType,
    onClick: onSectionCrumbClick
  };

  const contentCrumb = {
    content: contentType,
    onClick: null
  };

  const headerCrumb = {
    content: 'Edit Header',
    onClick: onHeaderCrumbClick
  };

  const pageTitleCrumb = {
    content: 'Edit Site Title',
    onClick: null
  };

  const navigationCrumb = {
    content: 'Edit Navigation',
    onClick: onNavigationCrumbClick
  };

  const menuItemCrumb = {
    content: menuItemType,
    onClick: null
  };

  const styleCrumb = {
    content: 'Styling',
    onClick: onStyleCrumbClick
  };

  const themeCrumb = {
    content: 'Themes',
    onClick: null
  };

  const pageScopedCssCrumb = {
    content: 'Page CSS',
    onClick: null
  };

  const languagesCrumb = {
    content: 'Languages',
    onClick: null
  };

  var crumbs = [];

  if (isEditingSection) {
    crumbs.push(subpageCrumb);
    crumbs.push(sectionCrumb);

    if (isEditingContent && !isEditingMultipleContentItems) {
      crumbs.push(contentCrumb);
    }

  } else if (isEditingHeader) {
    crumbs.push(headerCrumb);

    if (isEditingPageTitle) {
      crumbs.push(pageTitleCrumb);
    }

  } else if (isEditingTranslations) {
    crumbs.push(pageEditorCrumb);
    crumbs.push(languagesCrumb);

  } else if (isManagingStyle) {
    crumbs.push(pageEditorCrumb);
    crumbs.push(styleCrumb);

    if (isSelectingTheme) {
      crumbs.push(themeCrumb);
    } else if (isEditingPageScopedCss) {
      crumbs.push(pageScopedCssCrumb);
    }

  } else if (isEditingMenuItem) {
    crumbs.push(navigationCrumb);
    crumbs.push(menuItemCrumb);

  }

  return crumbs;
}

const BreadcrumbNavigationWithCrumbs = props =>
  <BreadcrumbNavigation breadcrumbs={generateBreadcrumbs(props)} />;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BreadcrumbNavigationWithCrumbs);
